import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/MeteringRecordsService';
import {MeteringRecord} from '../types';
import {stripReadonlyFields} from '../../../utils/stripReadonlyFields';

export const useMeteringRecord = (meteringRecordId?: string) => {
    const queryClient = useQueryClient();

    const meteringRecordQuery = useQuery({
        queryKey: ['metering-record', meteringRecordId],
        queryFn: () => fetch(meteringRecordId!),
        enabled: !!meteringRecordId
    });

    const createMutation = useMutation({
        mutationFn: (values: MeteringRecord) => {
            return add(stripReadonlyFields(values));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['metering-records']});
        }
    });

    const updateMutation = useMutation({
        mutationFn: (values: MeteringRecord) => {
            if (!meteringRecordId) throw new Error('meteringRecordId is required parameter');
            return update(meteringRecordId, stripReadonlyFields(values) as MeteringRecord);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['metering-records']});
            queryClient.invalidateQueries({queryKey: ['metering-record', meteringRecordId]});
        }
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!meteringRecordId) throw new Error('meteringRecordId is required parameter');
            return deleteRecord(meteringRecordId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['metering-records']});
        }
    });

    return {
        record: meteringRecordQuery.data,
        loading: meteringRecordQuery.isLoading,
        fetchRecord: meteringRecordQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};
